/**
 * API клієнт для doc.production_service_acts — Акт виробничих послуг.
 * Permission: production.service_act.manage.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface ProductionServiceActItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity?: string;
  price: string;
}

export interface ProductionServiceActItem extends ProductionServiceActItemIn {
  id: string;
  amount: string;
}

export interface ProductionServiceActListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  service_amount: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface ProductionServiceAct extends ProductionServiceActListItem {
  contract_id: string | null;
  vat_amount: string;
  revenue_account_code: string | null;     // Счет доходов (деф.703)
  receivable_account_code: string | null;  // Счет расчетов (деф.361)
  cost_account_code: string | null;        // Счет затрат (деф.231, round-trip)
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  items: ProductionServiceActItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ProductionServiceActCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  customer_contractor_id: string;
  contract_id?: string | null;
  service_amount: string;
  vat_amount?: string;
  revenue_account_code?: string | null;     // Счет доходов (деф.703)
  receivable_account_code?: string | null;  // Счет расчетов (деф.361)
  cost_account_code?: string | null;        // Счет затрат (деф.231)
  comment?: string | null;
  project_id?: string | null;
  items?: ProductionServiceActItemIn[];
}

export type ProductionServiceActUpdate = Partial<Omit<ProductionServiceActCreate, 'organization_id'>>;

export const productionServiceActs = {
  list: (params?: {
    organization_id?: string;
    customer_contractor_id?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<ProductionServiceActListItem>>('/production-service-acts', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<ProductionServiceAct>(`/production-service-acts/${id}`).then((r) => r.data),
  create: (payload: ProductionServiceActCreate) =>
    api.post<ProductionServiceAct>('/production-service-acts', payload).then((r) => r.data),
  update: (id: string, payload: ProductionServiceActUpdate) =>
    api.patch<ProductionServiceAct>(`/production-service-acts/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/production-service-acts/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<ProductionServiceAct>(`/production-service-acts/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<ProductionServiceAct>(`/production-service-acts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/production-service-acts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
